import React, { useState, useEffect, useMemo } from 'react';
import {
  FaShieldAlt,
  FaBolt,
  FaHeart,
  FaStar,
  FaSkull,
  FaArrowRight,
  FaCrosshairs,
  FaPlusCircle,
} from 'react-icons/fa';
import { GiCrossedSwords } from 'react-icons/gi';
import AbilityTooltip from '../utils/AbilityTooltip';
import { getOrderCardImageSrc } from '../utils/orderCardImageSrc';

// Custom empty circle component for Shield ability
const EmptyCircle = ({ size = 18, style = {} }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={style}
  >
    <circle cx="12" cy="12" r="10" />
  </svg>
);

// Helper function to get abilities from order
function getAbilities(order) {
  if (!order) return [];
  // ✅ FIX: Check instanceAbilities first if hasInstanceModifications is true
  if (order.hasInstanceModifications && Array.isArray(order.instanceAbilities)) {
    return order.instanceAbilities;
  }
  if (Array.isArray(order.abilities)) return order.abilities;
  // Backend/enrichment may send abilities as JSON string; parse so icons show (e.g. Kendrex Toxengineer Protect)
  if (typeof order.abilities === 'string') {
    try {
      const parsed = JSON.parse(order.abilities);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  }
  if (typeof order.ability === "string") return [order.ability];
  if (Array.isArray(order.abilitiesList)) return order.abilitiesList;
  return [];
}

// Get ability icon component
// Backend Ability Relay grants "Mend wounds" (with space); support both for UI display
function getAbilityIcon(abilityName) {
  const iconMap = {
    'Protect': FaShieldAlt,
    'Shield': EmptyCircle,
    'Blitz': FaBolt,
    'Recover': FaHeart,
    'Redeem': FaStar,
    'Fell': FaSkull,
    'Penetrate': FaArrowRight,
    'Sniper': FaCrosshairs,
    'MendWounds': FaPlusCircle,
    'Mend wounds': FaPlusCircle, // Ability Relay Drone grants this exact string
  };
  return iconMap[abilityName] || null;
}

// Ability descriptions (matching highlightAbilities.js exactly)
const ABILITY_DESCRIPTIONS = {
  'Recover': 'Attack anything to heal your general for the same amount',
  'MendWounds': 'This unit heals completely at the beginning of your turn',
  'Mend wounds': 'This unit heals completely at the beginning of your turn',
  'Penetrate': 'Deal excess damage to the opponent',
  'Blitz': 'This unit can attack the turn it has been deployed',
  'Redeem': 'Get the effect when the unit dies',
  'Sniper': 'Instantly kill any unit without a shield',
  'Protect': 'This unit must be attacked first',
  'Shield': 'This unit can avoid damage once',
  'Fell': 'Kill a unit to get the effect',
};

/** One-line label for an equipped weapon (stats from description), for tooltip rows. */
function getEquippedWeaponLabel(weapon) {
  if (!weapon || typeof weapon !== 'object') {
    throw new Error('getEquippedWeaponLabel: weapon must be a non-null object');
  }
  const statMatch = weapon.description?.match(/([+-]?\d+)\/([+-]?\d+)/);
  if (statMatch) {
    const attackBonus = parseInt(statMatch[1], 10);
    const healthBonus = parseInt(statMatch[2], 10);
    let statText = '';
    if (attackBonus !== 0) statText += `+${attackBonus}`;
    if (healthBonus !== 0) statText += `/${healthBonus}`;
    return `[${weapon.name}] (${statText})`;
  }
  return `[${weapon.name}]`;
}

// Get tooltip text for an ability
function getAbilityTooltipText(abilityName) {
  // Handle both "MendWounds" and "Mend wounds" variations
  if (abilityName === 'MendWounds' || abilityName === 'Mend wounds') {
    return ABILITY_DESCRIPTIONS['Mend wounds'];
  }
  
  // Direct lookup
  const description = ABILITY_DESCRIPTIONS[abilityName];
  
  // If no description found, return a fallback
  if (!description) {
    console.warn(`No tooltip description found for ability: ${abilityName}`);
    return `${abilityName} ability`; // Fallback so tooltip still shows
  }
  
  return description;
}

// Component for ability icons on the right side of the order
function AbilityIndicator({ order, iconsRowRef = null, compact = false }) {
  const [forceUpdate, setForceUpdate] = useState(0);
  
  // Force re-render when abilities change (e.g., Shield consumed, or Ability Relay grant)
  const instanceAbilitiesKey = Array.isArray(order.instanceAbilities)
    ? order.instanceAbilities.join(',')
    : '';
  useEffect(() => {
    setForceUpdate(prev => prev + 1);
  }, [
    order.abilities,
    order.hasShield,
    order.weapons,
    order.hasInstanceModifications,
    instanceAbilitiesKey,
  ]);
  
  const abilities = getAbilities(order);
  
  // Show all abilities that have icons (hand and deployed). Dedupe and filter to those with icons.
  let abilitiesToShow = [...new Set(abilities)];
  // Don't show Shield icon if it was consumed
  if (order.hasShield === false && abilitiesToShow.includes('Shield')) {
    abilitiesToShow = abilitiesToShow.filter(ability => ability !== 'Shield');
  }
  const abilitiesWithIcons = abilitiesToShow.filter(ability => getAbilityIcon(ability) !== null);
  
  // Check if order has equipped weapons (for sword icon)
  const hasEquippedWeapons = order.weapons && order.weapons.length > 0;
  
  const weaponTooltipContent = useMemo(() => {
    const weapons = order.weapons || [];
    if (weapons.length === 0) return null;
    const thumbPx = 36;
    return (
      <>
        <div style={{ marginBottom: '8px', color: '#b2ebf2', fontWeight: 600 }}>
          Equipped weapons:
        </div>
        {weapons.map((weapon, index) => (
          <div
            key={weapon.instanceId != null ? String(weapon.instanceId) : `${weapon.id}-${index}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginBottom: index < weapons.length - 1 ? '8px' : 0,
            }}
          >
            <img
              src={getOrderCardImageSrc(weapon)}
              alt=""
              width={thumbPx}
              height={thumbPx}
              style={{
                width: `${thumbPx}px`,
                height: `${thumbPx}px`,
                objectFit: 'cover',
                borderRadius: '4px',
                border: '1px solid rgba(0, 188, 212, 0.45)',
                flexShrink: 0,
                background: '#111',
              }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <span style={{ wordBreak: 'break-word' }}>{getEquippedWeaponLabel(weapon)}</span>
          </div>
        ))}
      </>
    );
  }, [order.weapons]);
  
  // Only show indicator if there are abilities with icons or equipped weapons
  if (abilitiesWithIcons.length === 0 && !hasEquippedWeapons) {
    return null;
  }

  const rowStyle = compact
    ? {
        position: 'absolute',
        // Minimal card image is centered (80% x 140% of 200x100), so anchor inside that face.
        top: '-14px',
        left: '24px',
        display: 'flex',
        flexDirection: 'row',
        gap: '4px',
        alignItems: 'center',
        zIndex: 12,
        pointerEvents: 'auto',
        // Keep icon strip on the same compositor path as the animated attack card.
        willChange: 'transform',
      }
    : {
        position: 'absolute',
        top: '100px', // Move ability icons 5px down for readability
        left: '55px', // Move ability icons 5px to the right
        display: 'flex',
        flexDirection: 'row', // Horizontal layout for icons
        gap: '6px',
        alignItems: 'flex-start', // Align to left
        zIndex: 10001, // Above description area (10000) to ensure pointer events work
        pointerEvents: 'auto', // Ensure pointer events work
      };

  const iconSize = compact ? 20 : 24;
  const glyphSize = compact ? 14 : 16;
  
  return (
    <>
      {/* Ability icons container below title/cost box, aligned left */}
      <div
        ref={iconsRowRef}
        style={rowStyle}
      >
        {abilitiesWithIcons.map((ability, index) => {
          const IconComponent = getAbilityIcon(ability);
          if (!IconComponent) return null;
          
          const tooltipText = getAbilityTooltipText(ability);
          
          // Ensure tooltip text is always set - this is critical for tooltip to show
          const finalTooltipText = tooltipText || `${ability} ability`;
          
          return (
            <AbilityTooltip
              key={`${ability}-${index}`}
              tooltipText={finalTooltipText || 'Ability description'}
            >
              <span
                style={{
                  display: 'inline-flex',
                  position: 'relative',
                  width: `${iconSize}px`,
                  height: `${iconSize}px`,
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  borderRadius: '4px',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  cursor: 'help',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  transition: compact ? 'none' : 'all 0.2s ease',
                  verticalAlign: 'middle',
                }}
              >
                <IconComponent size={glyphSize} />
              </span>
            </AbilityTooltip>
          );
        })}
        {hasEquippedWeapons && weaponTooltipContent && (
          <AbilityTooltip
            key="weapons"
            tooltipContent={weaponTooltipContent}
          >
            <span
              style={{
                display: 'inline-flex',
                position: 'relative',
                width: `${iconSize}px`,
                height: `${iconSize}px`,
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                borderRadius: '4px',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                cursor: 'help',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                transition: compact ? 'none' : 'all 0.2s ease',
                verticalAlign: 'middle',
              }}
            >
              <GiCrossedSwords size={glyphSize} />
            </span>
          </AbilityTooltip>
        )}
      </div>
    </>
  );
}

export default AbilityIndicator;
